import logoDarkDefault from "@public/logo.svg";
import { pickT } from "@repo/shared";
import type {
  LocaleCode,
  PublicFlowInfo,
  SendAuthCodeResult,
  VerifyAuthCodeResult,
} from "@repo/shared/types";
import { AlertCircle, FileText, Loader2, Lock, Mail, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiErrorMessage } from "@/lib/api/_client";
import {
  getPublicFlowInfo,
  sendPublicFlowAuthCode,
  verifyPublicFlowAuthCode,
} from "@/lib/api/public-flow";
import { languageFromError, supportedLocaleOrNull } from "@/lib/api/public-flow/contact-info";
import { T } from "./page.i18n";

export default function PublicFlowStartPage() {
  const navigate = useNavigate();
  const params = useParams();
  const flowId = params.flowId as string;

  const [flowInfo, setFlowInfo] = useState<PublicFlowInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"info" | "verify">("info");

  // Translations in the flow's language: from the info response, or from a
  // refusal's error body (QL-17 issue 027); English only with no response.
  const [errorLanguage, setErrorLanguage] = useState<LocaleCode | null>(null);
  const t = pickT(T, flowInfo?.language || errorLanguage || "en_GB");

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [authMethod, setAuthMethod] = useState<"email" | "sms">("email");
  const [clientId, setClientId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");

  // Load flow info
  useEffect(() => {
    async function loadFlowInfo() {
      setLoading(true);
      let info: PublicFlowInfo;
      try {
        info = await getPublicFlowInfo(flowId);
      } catch (err) {
        // The API writes its refusal in the flow's language and says which one.
        // The fallback (network failure) is English: no flow language yet.
        const language = languageFromError(err);
        setErrorLanguage(language);
        setError(apiErrorMessage(err, pickT(T, language ?? "en_GB").flowNotFound));
        setLoading(false);
        return;
      }

      if (!info.hasPublicPages) {
        setErrorLanguage(supportedLocaleOrNull(info.language));
        setError(pickT(T, info.language || "en_GB").flowNotConfigured);
        setLoading(false);
        return;
      }

      setFlowInfo(info);
      setLoading(false);
    }

    loadFlowInfo();
  }, [flowId]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!name.trim()) {
      setError(t.pleaseEnterName);
      return;
    }

    if (authMethod === "email") {
      if (!email.trim()) {
        setError(t.pleaseEnterEmail);
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setError(t.pleaseEnterValidEmail);
        return;
      }
    } else {
      if (!phone.trim()) {
        setError(t.pleaseEnterPhone);
        return;
      }
    }

    setSending(true);

    let result: SendAuthCodeResult;
    try {
      result = await sendPublicFlowAuthCode(
        flowId,
        {
          name: name.trim(),
          email: authMethod === "email" ? email.trim() : undefined,
          phone: authMethod === "sms" ? phone.trim() : undefined,
        },
        authMethod,
      );
    } catch (err) {
      setError(apiErrorMessage(err, t.failedToSendCode));
      setSending(false);
      return;
    }

    if (!result.success || !result.data) {
      setError(result.error || t.failedToSendCode);
      setSending(false);
      return;
    }

    setClientId(result.data.clientId);
    setStep("verify");
    setSending(false);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!verificationCode.trim() || verificationCode.length !== 6) {
      setError(t.pleaseEnterCode);
      return;
    }

    if (!clientId) {
      setError(t.sessionExpired);
      return;
    }

    setVerifying(true);

    let result: VerifyAuthCodeResult;
    try {
      result = await verifyPublicFlowAuthCode(flowId, clientId, verificationCode);
    } catch (err) {
      setError(apiErrorMessage(err, t.failedToVerifyCode));
      setVerifying(false);
      return;
    }

    if (!result.success || !result.data) {
      setError(result.error || t.failedToVerifyCode);
      setVerifying(false);
      return;
    }

    // Redirect to public flow instance with session token
    // Extract publicToken from the session JWT to use in URL path
    // The session token itself is passed as a query param
    const sessionToken = result.data.sessionToken;

    // Decode the JWT to get the publicToken (we don't need to verify, just extract)
    const payload = JSON.parse(atob(sessionToken.split(".")[1]));
    const publicToken = payload.publicToken;

    navigate(`/p/${publicToken}?session=${sessionToken}`);
  };

  if (loading) {
    return (
      <div className="dark min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !flowInfo) {
    return (
      <div className="dark min-h-screen flex items-center justify-center p-4 bg-background">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!flowInfo) {
    return (
      <div className="dark min-h-screen flex items-center justify-center p-4 bg-background">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t.failedToLoad}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Get tenant logo (use dark logo since page is dark themed, with fallback)
  const tenantLogo = flowInfo.tenant?.logoDarkUrl || flowInfo.tenant?.logoLightUrl;
  const defaultLogo = logoDarkDefault;
  const logoSrc = tenantLogo || defaultLogo;

  return (
    <div className="dark min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            {tenantLogo ? (
              // biome-ignore lint/a11y/useAltText: logo image
              <img src={logoSrc} className="h-10 max-w-[140px] object-contain" />
            ) : (
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="w-6 h-6 text-primary" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">{flowInfo.name}</h1>
              {flowInfo.description && (
                <p className="text-sm text-muted-foreground mt-1">{flowInfo.description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {step === "info" ? (
          <Card>
            <CardHeader>
              <CardTitle>{t.getStarted}</CardTitle>
              <CardDescription>{t.chooseVerification}</CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSendCode} className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t.fullName} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.enterFullName}
                    required
                    disabled={sending}
                    autoFocus
                  />
                </div>

                <div className="space-y-3">
                  <Label>
                    {t.verificationMethod} <span className="text-destructive">*</span>
                  </Label>
                  <Tabs
                    value={authMethod}
                    onValueChange={(value) => setAuthMethod(value as "email" | "sms")}
                  >
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="email" className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {t.email}
                      </TabsTrigger>
                      <TabsTrigger value="sms" className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {t.sms}
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="email" className="mt-4 space-y-2">
                      <Label htmlFor="email">
                        {t.emailAddress} <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t.emailPlaceholder}
                        disabled={sending}
                        required={authMethod === "email"}
                      />
                      <p className="text-xs text-muted-foreground">{t.emailHint}</p>
                    </TabsContent>
                    <TabsContent value="sms" className="mt-4 space-y-2">
                      <Label htmlFor="phone">
                        {t.phoneNumber} <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder={t.phonePlaceholder}
                        disabled={sending}
                        required={authMethod === "sms"}
                      />
                      <p className="text-xs text-muted-foreground">{t.phoneHint}</p>
                    </TabsContent>
                  </Tabs>
                </div>

                <div className="pt-4">
                  <Button type="submit" className="w-full" loading={sending}>
                    {t.sendCode}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{t.enterCode}</CardTitle>
              <CardDescription>
                {t.codeSentTo} {authMethod === "email" ? email : phone}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleVerifyCode} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="code">
                    {t.verificationCode} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    required
                    disabled={verifying}
                    autoFocus
                    className="text-center text-2xl tracking-widest"
                  />
                  <p className="text-xs text-muted-foreground">{t.codeExpires}</p>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setStep("info");
                      setVerificationCode("");
                      setError(null);
                    }}
                    disabled={verifying}
                    className="flex-1"
                  >
                    {t.back}
                  </Button>
                  <Button type="submit" className="flex-1" loading={verifying}>
                    {t.verifyStart}
                  </Button>
                </div>

                <div className="text-center pt-2">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => {
                      setStep("info");
                      setVerificationCode("");
                      setError(null);
                    }}
                    disabled={verifying}
                    className="text-sm"
                  >
                    {t.didntReceive}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Security Notice */}
        <div className="mt-8 p-4 bg-muted rounded-lg flex items-start gap-3 text-sm">
          <Lock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="text-muted-foreground">
            <p className="font-medium">{t.secureConnection}</p>
            <p>{t.securityNotice}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
